import { lessonSource } from '../../../library/printing/cardReadiness';
import { safeGameUrl } from '../../../library/gameMedia';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../auth/contexts/AuthContext';
import {
  PORTFOLIO_AI_CREATION_TYPES,
  PortfolioAiCreationType,
  PortfolioEntry,
  visibleAiCreations,
  visiblePortfolioPhotos,
} from '../../classroom/types';
import { aiUploadQueue } from '../../classroom/aiUploadQueue';
import {
  AI_CREATION_TYPE_LABELS,
  detectAiCreationFromFile,
  detectAiCreationType,
  extractVideoPoster,
  websitePreviewUrl,
  creationDisplayName,
  youtubePosterUrl,
} from '../../classroom/aiCreation';
import { extractPdfPoster } from '../../classroom/pdfPoster';
import ClassroomModal from './ClassroomModal';
import AiGroupEditor from './AiGroupEditor';

interface AiCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  squadId: string;
  squadName: string;
  teamName: string;
  entry: PortfolioEntry | null;
  initialSourcePhotoIds?: string[];
}

const AiCreationModal: React.FC<AiCreationModalProps> = ({
  isOpen,
  onClose,
  squadId,
  squadName,
  teamName,
  entry,
  initialSourcePhotoIds,
}) => {
  const { currentUser, userData } = useAuth();
  const photos = visiblePortfolioPhotos(entry?.photos);

  const [uploadMode, setUploadMode] = useState<'single' | 'options' | 'shot' | 'manage'>('single');
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [urlInput, setUrlInput] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [type, setType] = useState<PortfolioAiCreationType>('link');
  const [typeLocked, setTypeLocked] = useState(false);
  const [posterFile, setPosterFile] = useState<File | Blob | null>(null);
  const [posterPreview, setPosterPreview] = useState<string | null>(null);
  const [autoPosterUrl, setAutoPosterUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [posterBroken, setPosterBroken] = useState(false);
  const [nominateForLibrary, setNominateForLibrary] = useState(false);
  const [libraryContentType, setLibraryContentType] = useState<'character' | 'world' | 'movie' | 'comic' | 'poster' | 'game' | 'audio' | 'model_3d'>('character');
  const [sourcePreview, setSourcePreview] = useState<{ url: string; left: number; top: number; size: number } | null>(null);

  const detectedFrom = file?.name || urlInput;

  useEffect(() => {
    if (!isOpen || !entry) return;
    const defaults =
      initialSourcePhotoIds && initialSourcePhotoIds.length
        ? initialSourcePhotoIds.filter((id) => photos.some((p) => p.id === id))
        : photos.map((p) => p.id);
    setSourceIds(defaults.length ? defaults : photos.map((p) => p.id));
    setUploadMode('single');
    setUrlInput('');
    setFile(null);
    setFilePreview(null);
    setType('link');
    setTypeLocked(false);
    setPosterFile(null);
    setPosterPreview(null);
    setAutoPosterUrl(null);
    setCaption('');
    setExtracting(false);
    setError(null);
    setPosterBroken(false);
    setNominateForLibrary(false);
    setLibraryContentType('character');
    setSourcePreview(null);
  }, [isOpen, entry?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (filePreview) URL.revokeObjectURL(filePreview);
      if (posterPreview && posterPreview.startsWith('blob:')) URL.revokeObjectURL(posterPreview);
    };
  }, [filePreview, posterPreview]);

  useEffect(() => {
    if (!isOpen) return;
    if (typeLocked) return;
    if (!detectedFrom.trim() && !file) {
      setType('link');
      return;
    }
    setType(file ? detectAiCreationFromFile(file) : detectAiCreationType(detectedFrom));
  }, [detectedFrom, file, isOpen, typeLocked]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setAutoPosterUrl(null);
      setExtracting(false);
      setPosterBroken(false);
      if (type === 'image') {
        if (file && file.type.startsWith('image/')) {
          setAutoPosterUrl(filePreview);
          return;
        }
        if (urlInput.trim()) {
          setAutoPosterUrl(urlInput.trim());
          return;
        }
      }
      if (type === 'video' && urlInput.trim()) {
        const yt = youtubePosterUrl(urlInput.trim());
        if (yt) setAutoPosterUrl(yt);
        else {
          const preview = websitePreviewUrl(urlInput.trim());
          if (preview) setAutoPosterUrl(preview);
        }
      }
      if (type === 'video' && file && file.type.startsWith('video/')) {
        setExtracting(true);
        try {
          const blob = await extractVideoPoster(file);
          if (cancelled) return;
          const next = URL.createObjectURL(blob);
          setPosterFile(blob);
          setPosterPreview((prev) => {
            if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return next;
          });
        } catch {
          /* Optional poster — teachers can save without a frame. */
        } finally {
          if (!cancelled) setExtracting(false);
        }
      }
      if (type === 'pdf' && file) {
        setExtracting(true);
        try {
          const blob = await extractPdfPoster(file);
          if (cancelled || !blob) return;
          const next = URL.createObjectURL(blob);
          setPosterFile(blob);
          setPosterPreview((prev) => {
            if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
            return next;
          });
        } catch {
          /* Filename fallback is enough for PDFs. */
        } finally {
          if (!cancelled) setExtracting(false);
        }
      }
      if (!file && urlInput.trim() && type !== 'image' && type !== 'audio') {
        if (type === 'video' && youtubePosterUrl(urlInput.trim())) return;
        const preview = websitePreviewUrl(urlInput.trim());
        if (preview && !cancelled) setAutoPosterUrl((current) => current || preview);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [type, file, filePreview, urlInput]);

  useEffect(() => {
    if (!nominateForLibrary) return;
    if (['game_link', 'claude_artifact', 'link'].includes(type)) setLibraryContentType('game');
    if (type === 'audio') setLibraryContentType('audio');
    if (type === 'video' && !['movie', 'model_3d'].includes(libraryContentType)) setLibraryContentType('movie');
    if (type === 'image' && ['movie', 'game', 'audio'].includes(libraryContentType)) setLibraryContentType('character');
  }, [type, nominateForLibrary, libraryContentType]);

  const resolvedPoster = posterPreview || autoPosterUrl;
  const showPoster = Boolean(resolvedPoster && !posterBroken);
  const canSave = Boolean((file || urlInput.trim()) && sourceIds.length && !extracting);

  const toggleSource = (id: string) => {
    setSourceIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const showSourcePreview = (url: string, target: HTMLImageElement) => {
    const rect = target.getBoundingClientRect();
    const size = Math.min(420, window.innerWidth - 32, window.innerHeight - 32);
    const left = rect.right + 12 + size <= window.innerWidth - 16
      ? rect.right + 12
      : Math.max(16, rect.left - size - 12);
    const top = Math.max(16, Math.min(rect.top, window.innerHeight - size - 16));
    setSourcePreview({ url, left, top, size });
  };

  const onPickFile = (next: File | null) => {
    setFilePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return next ? URL.createObjectURL(next) : null;
    });
    setFile(next);
    setTypeLocked(false);
    if (next) setUrlInput('');
    setPosterFile(null);
    setPosterPreview((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const onPickPoster = (next: File | null) => {
    setPosterPreview((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return next ? URL.createObjectURL(next) : null;
    });
    setPosterFile(next);
    setPosterBroken(false);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!entry || !currentUser?.uid || !canSave) return;
    if (!sourceIds.length) {
      setError('Pick at least one source photo.');
      return;
    }
    if (nominateForLibrary && !['movie', 'game', 'audio', 'model_3d'].includes(libraryContentType) && type !== 'image') {
      setError('This card type needs an image.');
      return;
    }
    if (nominateForLibrary && libraryContentType === 'model_3d' && !['image', 'video'].includes(type)) { setError('Use an image or video preview for a 3D model card.'); return; }
    if (nominateForLibrary && libraryContentType === 'audio' && type !== 'audio') { setError('Use an audio file for an audio card.'); return; }
    if (nominateForLibrary && libraryContentType === 'game' && !safeGameUrl(urlInput.trim())) { setError('Enter an http or https game link.'); return; }
    if (nominateForLibrary && libraryContentType === 'movie' && type !== 'video') {
      setError('Movies can only be nominated from a video.');
      return;
    }
    setError(null);
    try {
      const alreadyRevealed = visibleAiCreations(entry.aiVersions).some((a) => a.status === 'revealed');
      const uploadPoster = type === 'image' ? null : posterFile;
      const filename = creationDisplayName({ file, url: urlInput.trim(), type });
      aiUploadQueue.enqueue({
        squadId,
        entryId: entry.id,
        teamId: entry.teamId,
        sessionId: entry.sessionId,
        type,
        url: file ? null : urlInput.trim(),
        file,
        posterFile: uploadPoster,
        posterUrl: (uploadPoster || type === 'image' || posterBroken) ? null : autoPosterUrl,
        filename,
        caption,
        sourcePhotoIds: sourceIds,
        uploadedBy: currentUser.uid,
        uploadedByName: String(userData?.displayName || currentUser.displayName || 'Staff').trim(),
        revealed: alreadyRevealed,
        nominateForLibrary,
        libraryContentType,
        sourceLesson: lessonSource(entry).sourceLesson,
        squadName,
        teamName,
      }, filename);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not start the upload.');
    }
  };

  const detectedHint = useMemo(() => {
    if (!detectedFrom.trim() && !file) return 'Paste a URL or upload a file — we’ll detect the type.';
    const detected = file ? detectAiCreationFromFile(file) : detectAiCreationType(detectedFrom);
    return `Detected as ${AI_CREATION_TYPE_LABELS[detected]}. You can override.`;
  }, [detectedFrom, file]);

  if (isOpen && entry && uploadMode !== 'single') return <AiGroupEditor squadId={squadId} entry={entry} sourcePhotoIds={sourceIds} mode={uploadMode} onClose={onClose} />;

  return (
    <ClassroomModal isOpen={isOpen} title="Add AI creation" onClose={onClose} width={560}>
      <form onSubmit={submit} className="cd-modal-form">
        <div className="cd-group-tools" role="group" aria-label="Upload format">
          <button type="button" className="cd-btn cd-btn-primary" aria-pressed="true">Single creation</button>
          <button type="button" className="cd-btn" onClick={() => setUploadMode('options')}>Multiple options</button>
          <button type="button" className="cd-btn" onClick={() => setUploadMode('shot')}>Storyboard</button>
          {!!entry?.aiGroups?.length && <button type="button" className="cd-btn" onClick={() => setUploadMode('manage')}>Edit groups</button>}
        </div>
        <p className="cd-field-help">
          Link this creation to the paper photos it was made from, then paste a URL or upload the file.
        </p>

        {photos.length > 0 && (
          <div className="cd-field">
            <span className="cd-field-label">Source photos</span>
            <div className="cd-ai-sources">
              {photos.map((photo, index) => {
                const checked = sourceIds.includes(photo.id);
                return (
                  <label key={photo.id} className={`cd-ai-source ${checked ? 'is-on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSource(photo.id)}
                    />
                    <img
                      src={photo.url}
                      alt=""
                      onMouseEnter={(event) => showSourcePreview(photo.url, event.currentTarget)}
                      onMouseLeave={() => setSourcePreview(null)}
                    />
                    <span>photo {index + 1}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {sourcePreview && createPortal(
          <div
            className="cd-ai-source-preview"
            style={{ left: sourcePreview.left, top: sourcePreview.top, width: sourcePreview.size, height: sourcePreview.size }}
            aria-hidden="true"
          >
            <img src={sourcePreview.url} alt="" />
          </div>,
          document.body
        )}

        <label className="cd-field">
          <span className="cd-field-label">URL</span>
          <input
            className="cd-input"
            type="url"
            placeholder="https://…"
            value={urlInput}
            onChange={(event) => {
              setUrlInput(event.target.value);
              setTypeLocked(false);
              if (event.target.value.trim()) onPickFile(null);
            }}
          />
        </label>

        <div className="cd-field">
          <span className="cd-field-label">Or upload a file</span>
          <input
            className="cd-input cd-input-file"
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.glb,.gltf,.stl,application/pdf"
            onChange={(event) => onPickFile(event.target.files?.[0] || null)}
          />
          {file ? null : <span className="cd-field-help">Video, image, PDF, 3D, or other files</span>}
        </div>

        <label className="cd-field">
          <span className="cd-field-label">Type</span>
          <select
            className="cd-input"
            value={type}
            onChange={(event) => {
              setType(event.target.value as PortfolioAiCreationType);
              setTypeLocked(true);
            }}
          >
            {PORTFOLIO_AI_CREATION_TYPES.map((item) => (
              <option key={item} value={item}>
                {AI_CREATION_TYPE_LABELS[item]}
              </option>
            ))}
          </select>
          <span className="cd-field-help">{detectedHint}</span>
        </label>

        <div className="cd-field">
          <span className="cd-field-label">Poster (optional)</span>
          {showPoster ? (
            <img
              className="cd-ai-poster-preview"
              src={resolvedPoster || ''}
              alt="Poster preview"
              onError={() => setPosterBroken(true)}
            />
          ) : (
            <div className="cd-ai-poster-empty">
              {extracting
                ? type === 'pdf'
                  ? 'Grabbing first page…'
                  : 'Grabbing first frame…'
                : creationDisplayName({ file, url: urlInput.trim(), type })}
            </div>
          )}
          <input
            className="cd-input cd-input-file"
            type="file"
            accept="image/*"
            onChange={(event) => onPickPoster(event.target.files?.[0] || null)}
          />
          {type === 'image' && (
            <span className="cd-field-help">Images use themselves as the poster.</span>
          )}
          {type === 'video' && (
            <span className="cd-field-help">
              We’ll try the first frame, a YouTube thumbnail, or a page snapshot. You can save without one.
            </span>
          )}
          {type === 'pdf' && (
            <span className="cd-field-help">
              We’ll try the first page. If that doesn’t work, the PDF name is enough — no extra upload needed.
            </span>
          )}
          {(type === 'link' || type === 'game_link' || type === 'claude_artifact' || type === 'model_3d') && (
            <span className="cd-field-help">
              We’ll try a snapshot of the page. If that doesn’t load, the site name is enough — no extra upload needed.
            </span>
          )}
        </div>

        <label className="cd-field">
          <span className="cd-field-label">Caption (optional)</span>
          <input
            className="cd-input"
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
            maxLength={200}
            placeholder="Short note about this creation"
          />
        </label>

        <div className="cd-field cd-library-nomination">
          <span className="cd-field-label">Nominate for the Universe?</span>
          <div className="cd-showdown-kind" role="group" aria-label="Nominate for the Universe">
            <button type="button" className={!nominateForLibrary ? 'is-on' : ''} onClick={() => setNominateForLibrary(false)}>No</button>
            <button type="button" className={nominateForLibrary ? 'is-on' : ''} onClick={() => setNominateForLibrary(true)}>Yes</button>
          </div>
          {nominateForLibrary && <label>
            <span className="cd-field-label">Universe type</span>
            <select className="cd-input" value={libraryContentType} onChange={event => setLibraryContentType(event.target.value as 'character' | 'world' | 'movie' | 'comic' | 'poster' | 'game' | 'audio' | 'model_3d')}>
              <option value="character" disabled={type !== 'image'}>Character — image only</option>
              <option value="world" disabled={type !== 'image'}>World — image only</option>
              <option value="movie" disabled={type !== 'video'}>Video</option>
              <option value="comic" disabled={type !== 'image'}>Comic</option>
              <option value="poster" disabled={type !== 'image'}>Poster</option>
              <option value="audio" disabled={type !== 'audio'}>Audio</option>
              <option value="model_3d" disabled={!['image', 'video'].includes(type)}>3D model — image or video preview</option>
              <option value="game" disabled={!['game_link', 'claude_artifact', 'link'].includes(type)}>Video game</option>
            </select>
            <span className="cd-field-help">The upload will enter the admin review queue. Student names are never included.</span>
          </label>}
        </div>

        <div className="cd-modal-footer">
          {error && <p className="cd-modal-error">{error}</p>}
          <div className="cd-modal-actions">
            <button type="button" className="cd-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="cd-btn cd-btn-primary" disabled={!canSave}>
              Start upload
            </button>
          </div>
        </div>
      </form>
    </ClassroomModal>
  );
};

export default AiCreationModal;
